const GOOGLE_CLIENT_ID =
  '996058902646-au2c1g41ipkkskdflqt5sk71k6ri28dj.apps.googleusercontent.com';

const PRODUCTION_BACKEND_URL = 'https://api.fitcutter.com';
const LOCAL_BACKEND_URL = 'http://localhost:8080';

const BACKEND_OVERRIDE_KEY = 'fitcutter_backend_url';
const LOCAL_KEY = 'fitcutter_local';

/**
 * Injected by webpack's DefinePlugin. True only for a local build:
 *   npm run dev:local      (or FITCUTTER_LOCAL=1 npm run dev)
 *
 * Local mode changes exactly two things, and nothing else:
 *   1. the API points at localhost instead of production
 *   2. Paddle is not loaded (its client token is a LIVE one)
 *
 * Every other behaviour — the paid-plan gate on the passkey and the branding
 * editor included — is identical to production. To exercise a paid feature
 * locally, give your local account a paid plan in the database; do not special-case
 * the app.
 *
 * Each resolver below returns its production answer immediately when this is
 * false, so DefinePlugin folds the switches away and a release bundle contains
 * no way to reach them. That is deliberate: the overrides read from the query
 * string and persist to localStorage, so shipping them would let a crafted
 * fitcutter.com link permanently repoint this origin's API — and every request
 * here carries the Google ID token.
 */
declare const __LOCAL_BUILD__: boolean;

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    // Private mode / storage disabled.
    return null;
  }
}

function writeStorage(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Not persistable — the value still applies to this page load.
  }
}

/**
 * Local mode, in priority order:
 *   1. ?local=1 / ?local=0  — applied and remembered
 *   2. a stored flag
 *   3. the build flag
 *
 * Local builds only. A release build is never in local mode, so `?local=1` on
 * fitcutter.com cannot switch checkout off for whoever opened the link.
 */
function resolveLocal(): boolean {
  if (!__LOCAL_BUILD__) return false;

  const fromQuery = new URLSearchParams(window.location.search).get('local');
  if (fromQuery !== null) {
    const on = fromQuery !== '0' && fromQuery !== 'false';
    writeStorage(LOCAL_KEY, on ? '1' : null);
    return on;
  }

  const stored = readStorage(LOCAL_KEY);
  if (stored !== null) return stored === '1';

  return __LOCAL_BUILD__;
}

const IS_LOCAL = resolveLocal();

/**
 * Which API this page talks to, in priority order:
 *   1. ?api=<url|local|prod>  — applied and remembered
 *   2. a stored override      — fitcutter.useBackend(...)
 *   3. local mode → localhost, otherwise production
 *
 * Local builds only, and this is the important one: `?api=` takes an arbitrary
 * URL and persists it, while api.ts sends the Google ID token — and, on
 * register, the YouTube access token — to whatever it resolves to. A release
 * build answers PRODUCTION_BACKEND_URL and reads neither the query nor storage.
 */
function resolveBackendUrl(): string {
  if (!__LOCAL_BUILD__) return PRODUCTION_BACKEND_URL;

  const fromQuery = new URLSearchParams(window.location.search).get('api');
  if (fromQuery) {
    const url = fromQuery === 'local'
      ? LOCAL_BACKEND_URL
      : fromQuery === 'prod' ? PRODUCTION_BACKEND_URL : fromQuery.replace(/\/$/, '');
    writeStorage(BACKEND_OVERRIDE_KEY, url);
    return url;
  }

  const stored = readStorage(BACKEND_OVERRIDE_KEY);
  if (stored) return stored.replace(/\/$/, '');

  return IS_LOCAL ? LOCAL_BACKEND_URL : PRODUCTION_BACKEND_URL;
}

const BACKEND_URL = resolveBackendUrl();

/**
 * Paddle is a LIVE production account — real checkouts, real cards. Local mode
 * keeps the SDK out of the page entirely rather than pointing it at localhost.
 * Force it back on with ?paddle=1.
 *
 * Local builds only. Checkout is never suppressed in a release build.
 */
function resolvePaddleDisabled(): boolean {
  if (!__LOCAL_BUILD__) return false;

  const fromQuery = new URLSearchParams(window.location.search).get('paddle');
  if (fromQuery !== null) {
    return fromQuery === '0' || fromQuery === 'false';
  }
  return IS_LOCAL;
}

const PADDLE_CLIENT_TOKEN = 'live_3afeccf52e4b43bbe1cd2d2648a';
const PADDLE_ENV = 'production' as const;
const PADDLE_STARTER_PRICE_ID = 'pri_01kkj0qe0jfm2s3qft0ywcfb2v';
const PADDLE_UNLIMITED_PRICE_ID = 'pri_01kkj0rvn7g9evn343dxqa7f6n';

export const Config = {
  GOOGLE_CLIENT_ID,
  BACKEND_URL,
  PRODUCTION_BACKEND_URL,
  LOCAL_BACKEND_URL,
  BACKEND_OVERRIDE_KEY,
  LOCAL_KEY,
  IS_PRODUCTION_BACKEND: BACKEND_URL === PRODUCTION_BACKEND_URL,
  IS_LOCAL_BUILD: __LOCAL_BUILD__,
  IS_LOCAL,
  /** True when checkout is switched off — see resolvePaddleDisabled. */
  PADDLE_DISABLED: resolvePaddleDisabled(),
  PADDLE_CLIENT_TOKEN,
  PADDLE_ENV,
  PADDLE_STARTER_PRICE_ID,
  PADDLE_UNLIMITED_PRICE_ID,
} as const;
